import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import FileSaver from "file-saver";

import {
    Box,
    Breadcrumbs,
    Card,
    CardContent,
    CardHeader,
    CardMedia,
    Chip,
    Container,
    Grid,
    Tooltip,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import CourseEnrolmentDetails from "./CourseEnrolmentDetails";
import DayJS from "react-dayjs";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";
import Fetching from "../../../Common/Fetching";
import Module from "../Module/Module";
import SkillChip from "../SkillChip";

import TMSAvatar from "../TMSAvatar";

import { ActionTypes, DataTypes } from "../../../../constants";
import { getCourseDetails, getError, isFetching } from "../../../../reducers";
import tmsActions from "../../../../actions/tmsActions";

const appCode = "C-TMS";

const DurationText = ({ duration }) => {
    let displayDuration = "";
    const durationNumber = parseInt(duration);

    if (durationNumber > 0) {
        let hours = Math.floor(Number(durationNumber) / 60);
        let minutes = Number(durationNumber) % 60;
        if (hours > 0) {
            displayDuration = ` (${hours} hr`;
            if (hours > 1) {
                displayDuration += `s`;
            }
            if (minutes > 0) {
                displayDuration += ` ${minutes} min`;
            }
            displayDuration += `)`;
        } else if (minutes > 0) {
            displayDuration = ` (${minutes} min)`;
        }
    }

    return displayDuration;
};

function Course({
    course,
    downloadAsset,
    onCompleteModule,
    onRetakeModule,
    isFetching,
}) {
    const title = course?.name ?? "Course title";
    const code = course?.code ?? "";
    const validFrom = course?.validFrom ?? "";
    const [showModules, setShowModules] = useState(false);

    const handleAssetDownload = ({ id, file }) =>
        downloadAsset(id, "C-TMS", (blob) => FileSaver.saveAs(blob, file));

    let enrolment = {
        isActive:
            course?.enrolment?.status &&
            (course.enrolment.status === "enrolled" ||
                course.enrolment.status === "completed"),
        status: course?.enrolment?.status ?? null,
        message: "",
        dateCompleted: course?.enrolment?.dateCompleted ?? null,
    };

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" to="/">
                            Home
                        </Link>
                        <Link underline="hover" color="inherit" to="/tms">
                            Training
                        </Link>
                        <Link
                            underline="hover"
                            color="inherit"
                            to="/tms/library"
                        >
                            Library
                        </Link>

                        {!!course?.category && (
                            <Link
                                underline="hover"
                                color="inherit"
                                to={`/tms/library/${course?.category?.id}`}
                            >
                                {course?.category?.name || "Current"}
                            </Link>
                        )}
                    </Breadcrumbs>
                </Grid>

                {!!isFetching && (
                    <Grid item xs={12}>
                        <Fetching />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Card className="card">
                        {!!course?.image && (
                            <CardMedia
                                component="img"
                                height="300"
                                image={course.image}
                                alt=""
                            />
                        )}
                        <CardHeader
                            disableTypography={true}
                            avatar={
                                <TMSAvatar
                                    type="course"
                                    badge={
                                        course?.enrolment?.status ===
                                        "completed"
                                            ? "checked"
                                            : course?.enrolment?.status ===
                                              "enrolled"
                                            ? "checked-blue"
                                            : ""
                                    }
                                />
                            }
                            title={
                                <Typography
                                    component="h2"
                                    className="card-header-title"
                                >
                                    {title} - {code}
                                </Typography>
                            }
                            subheader={
                                <Typography
                                    component="span"
                                    className="card-header-subtitle"
                                >
                                    Released on{" "}
                                    <DayJS format="DD MMM YYYY">
                                        {validFrom}
                                    </DayJS>
                                </Typography>
                            }
                        />

                        {!!course?.description && (
                            <CardContent>
                                <Box className="card-content">
                                    <Typography variant="h3" component="h3">
                                        Description
                                    </Typography>
                                    <Typography variant="body1">
                                        {course.description}
                                    </Typography>
                                </Box>
                            </CardContent>
                        )}

                        {!!course?.overview && (
                            <CardContent>
                                <Box className="card-content">
                                    <Typography variant="h3" component="h3">
                                        Overview
                                    </Typography>
                                    <Typography variant="body1">
                                        {course.overview}
                                    </Typography>
                                </Box>
                            </CardContent>
                        )}

                        {!!course?.modules?.length > 0 && (
                            <CardContent>
                                <Box className="card-content">
                                    This course consists of{" "}
                                    {course.modules.length}
                                    {course.modules.length > 1
                                        ? " modules"
                                        : " module"}
                                    .
                                    <DurationText duration={course.duration} />
                                </Box>
                            </CardContent>
                        )}

                        {!!course?.skill && (
                            <CardContent>
                                <Box className="card-content">
                                    <SkillChip label={course.skill.name} />
                                </Box>
                            </CardContent>
                        )}

                        {!!course?.assets?.length > 0 && (
                            <CardContent>
                                <Box className="card-content">
                                    <fieldset>
                                        <legend>
                                            <Typography component="h3">
                                                Associated files
                                                <span className="sr-only">
                                                    &nbsp; for {title}
                                                </span>
                                            </Typography>
                                        </legend>
                                        <Box>
                                            {!course || !course.assets
                                                ? null
                                                : course.assets.map((asset) => (
                                                      <Tooltip
                                                          title="download file"
                                                          arrow
                                                          key={asset.id}
                                                      >
                                                          <Chip
                                                              key={asset.id}
                                                              label={asset.name}
                                                              aria-label={`Download file ${asset.name}`}
                                                              className="chip"
                                                              variant="outlined"
                                                              color="primary"
                                                              icon={
                                                                  <DownloadForOfflineIcon
                                                                      sx={{
                                                                          fontSize: 26,
                                                                      }}
                                                                  />
                                                              }
                                                              onClick={() =>
                                                                  handleAssetDownload(
                                                                      asset
                                                                  )
                                                              }
                                                          />
                                                      </Tooltip>
                                                  ))}
                                        </Box>
                                    </fieldset>
                                </Box>
                            </CardContent>
                        )}

                        {!!course?.url && (
                            <CardContent>
                                <Box className="card-content">
                                    <Typography component="h3">Link</Typography>
                                    <Typography variant="body1">
                                        <a
                                            href={course.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {course.url}
                                        </a>
                                    </Typography>
                                </Box>
                            </CardContent>
                        )}
                        {!!course && <CourseEnrolmentDetails course={course} />}
                        {!!course?.modules?.length > 0 && (
                            <CardContent>
                                <Box className="card-content">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disableElevation={true}
                                        onClick={() =>
                                            setShowModules(!showModules)
                                        }
                                    >
                                        {!showModules
                                            ? "Show modules"
                                            : "Hide modules"}
                                    </Button>
                                </Box>
                            </CardContent>
                        )}
                    </Card>
                </Grid>

                {showModules &&
                    !!course?.modules?.length > 0 &&
                    course.modules
                        .sort((a, b) => a.listOrder - b.listOrder)
                        .map((module) => (
                            <Grid item lg={12} xl={6} key={module.id}>
                                <Module
                                    module={module}
                                    isActive={!!enrolment.isActive}
                                    onAssetDownload={handleAssetDownload}
                                    onComplete={onCompleteModule}
                                    onRetake={onRetakeModule}
                                />
                            </Grid>
                        ))}
            </Grid>
        </Container>
    );
}

const CourseContainer = ({ match }) => {
    const dispatch = useDispatch();
    const course = useSelector((state) =>
        getCourseDetails(state, Number(match.params.id))
    );
    const error = useSelector((state) =>
        getError(state, ActionTypes.GET_TMS_COURSE_DETAILS_SUCCESS)
    );
    const isFetchingState = useSelector((state) =>
        isFetching(state, DataTypes.TMS_COURSE_DETAILS, match.params.id)
    );

    useEffect(() => {
        dispatch(tmsActions.getCourseDetails(match.params.id, appCode));
    }, [dispatch, match.params.id]);

    if (!!error) {
        if (error.errorCode === 404) {
            return <Error404 />;
        }
        if (error.errorCode === 500) {
            return <Error500 />;
        }
    }

    const getCourseDetailsCallback = () => {
        dispatch(tmsActions.getCourseDetails(match.params.id, appCode));
    };

    return (
        <Course
            course={course}
            downloadAsset={(id, appCode, callback) =>
                dispatch(tmsActions.downloadAsset(id, appCode, callback))
            }
            onCompleteModule={getCourseDetailsCallback}
            onRetakeModule={getCourseDetailsCallback}
            isFetching={isFetchingState}
        />
    );
};

export default CourseContainer;
